
import React, { useState } from "react";
import { X, Filter, Check, ChevronDown } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import { Label } from "../../ui/label";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";

// Numeric filter fields for this table
const NUMERIC_FIELDS = ["average_price", "units_sold", "revenue"];

interface EcommPerformanceFilterProps {
  isOpen: boolean;
  onClose: () => void;
  filters: Record<string, string[]>;
  setFilters: (f: Record<string, string[]>) => void;
  options: Record<string, string[]>;
  numericRanges: Record<string, [number?, number?]>;
  setNumericRanges: (r: Record<string, [number?, number?]>) => void;
}

export function EcommPerformanceFilter({
  isOpen,
  onClose,
  filters,
  setFilters,
  options,
  numericRanges,
  setNumericRanges
}: EcommPerformanceFilterProps) {
  const [search, setSearch] = useState<Record<string, string>>({});
  const [localFilters, setLocalFilters] = useState<Record<string, string[]>>({});
  const [localNumericRanges, setLocalNumericRanges] = useState<Record<string, [number?, number?]>>({});
  const fields = Object.keys(options);

  // Sync local state with parent when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
      setLocalNumericRanges(numericRanges);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  // Handlers for categorical filters (local)
  const handleToggle = (field: string, value: string) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: prev[field]?.includes(value)
        ? prev[field].filter(v => v !== value)
        : [...(prev[field] || []), value]
    }));
  };
  const handleClear = (field: string) => {
    setLocalFilters(prev => ({ ...prev, [field]: [] }));
  };
  const handleSelectAll = (field: string) => {
    setLocalFilters(prev => ({ ...prev, [field]: options[field] }));
  };
  const getFiltered = (field: string) =>
    options[field].filter(opt =>
      (search[field] || "").length === 0 || opt.toLowerCase().includes(search[field].toLowerCase())
    );

  // Numeric range handlers (local)
  const handleNumericChange = (field: string, idx: 0 | 1, value: string) => {
    const num = value ? Number(value) : undefined;
    const prev: [number?, number?] = localNumericRanges[field] || [undefined, undefined];
    const next: [number?, number?] = [...prev];
    next[idx] = num;
    setLocalNumericRanges({ ...localNumericRanges, [field]: next });
  };
  const handleClearNumeric = (field: string) => {
    setLocalNumericRanges(prev => ({ ...prev, [field]: [undefined, undefined] }));
  };

  const getActiveFiltersCount = () =>
    fields.reduce((acc, f) => acc + (localFilters[f] || []).length, 0) +
    NUMERIC_FIELDS.reduce((acc, f) => acc + ((localNumericRanges[f]?.[0] !== undefined || localNumericRanges[f]?.[1] !== undefined ? 1 : 0)), 0);

  // Apply local filters to parent on Apply Filters
  const handleApply = () => {
    setFilters(localFilters);
    setNumericRanges(localNumericRanges);
    onClose();
  };

  // Reset all local filters
  const handleResetAll = () => {
    const cleared: Record<string, string[]> = {};
    fields.forEach(f => { cleared[f] = []; });
    setLocalFilters(cleared);
    const clearedNum: Record<string, [number?, number?]> = {};
    NUMERIC_FIELDS.forEach(f => { clearedNum[f] = [undefined, undefined]; });
    setLocalNumericRanges(clearedNum);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Filter Performance</h2>
            {getActiveFiltersCount() > 0 && (
              <Badge variant="secondary" className="ml-2">
                {getActiveFiltersCount()} active
              </Badge>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" aria-label="Close filter">
            <X className="h-4 w-4" />
          </Button>
        </div>
        {/* Content */}
        <div className="p-4 space-y-6 max-h-[calc(90vh-140px)] overflow-y-auto">
          {/* Numeric Ranges */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-card-foreground border-b border-border/40 pb-2">
              Numeric Ranges
            </h3>
            {NUMERIC_FIELDS.map(field => {
              const filterValue = localNumericRanges[field] || [undefined, undefined];
              const hasFilter = filterValue[0] !== undefined || filterValue[1] !== undefined;
              return (
                <div key={field} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-card-foreground font-medium">
                      {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </label>
                    {hasFilter && (
                      <button
                        className="text-xs text-muted-foreground hover:text-card-foreground"
                        onClick={() => handleClearNumeric(field)}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1">
                      <input
                        type="number"
                        placeholder="Min"
                        value={filterValue[0] ?? ""}
                        onChange={e => handleNumericChange(field, 0, e.target.value)}
                        className="w-full border border-border/60 rounded-lg px-3 py-2 bg-background text-card-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200"
                      />
                    </div>
                    <span className="text-muted-foreground font-medium">to</span>
                    <div className="flex-1">
                      <input
                        type="number"
                        placeholder="Max"
                        value={filterValue[1] ?? ""}
                        onChange={e => handleNumericChange(field, 1, e.target.value)}
                        className="w-full border border-border/60 rounded-lg px-3 py-2 bg-background text-card-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Categorical Filters */}
          {fields.filter(f => !NUMERIC_FIELDS.includes(f)).map(field => (
            <div className="space-y-3" key={field}>
              <Label className="text-sm font-medium capitalize">{field.replace(/_/g, ' ')}</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between min-h-10"
                  >
                    <span className="truncate">
                      {localFilters[field]?.length > 0
                        ? `${localFilters[field].length} selected`
                        : `Select ${field.replace(/_/g, ' ')}`}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-80 p-0">
                  <div className="p-2 border-b">
                    <Input
                      placeholder={`Search ${field}...`}
                      value={search[field] || ""}
                      onChange={e => setSearch(s => ({ ...s, [field]: e.target.value }))}
                      className="h-8"
                    />
                  </div>
                  <div className="p-2 border-b flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={() => handleSelectAll(field)}
                    >
                      Select All
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={() => handleClear(field)}
                    >
                      Clear All
                    </Button>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {getFiltered(field).map(opt => (
                      <DropdownMenuCheckboxItem
                        key={opt}
                        checked={localFilters[field]?.includes(opt)}
                        onCheckedChange={() => handleToggle(field, opt)}
                        className="pl-8"
                      >
                        {opt}
                      </DropdownMenuCheckboxItem>
                    ))}
                    {getFiltered(field).length === 0 && (
                      <div className="py-2 px-4 text-sm text-muted-foreground">No options found</div>
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
              {localFilters[field]?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1 max-h-20 overflow-y-auto">
                  {localFilters[field].map(val => (
                    <Badge key={val} variant="secondary" className="text-xs">
                      {val}
                      <button type="button" className="ml-1 hover:text-destructive" onClick={() => handleToggle(field, val)} aria-label={`Remove ${val}`}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-border bg-muted/50">
          <Button
            variant="outline"
            onClick={handleResetAll}
            disabled={getActiveFiltersCount() === 0}
          >
            Reset All
          </Button>
          <Button onClick={handleApply} className="min-w-20">
            <Check className="w-4 h-4 mr-2" />Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
}
